import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  createConfigurableAjv,
  validateData,
} from '@web-config-app/validation';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import { ConfigErrorType, getAllFeatureFlags } from '@web-config-app/core';
import {
  useConfigUserFeedback,
  useConfigApp,
  removeNullAndEmptyObjects,
} from '@web-config-app/core-react';
import {
  applyConditionalLogic,
  computeSchema,
} from '@web-config-app/schema-utils';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';
import { useFormatFormErrors } from '../use-format-form-errors/use-format-form-errors.hook';
import { useLocalizedFieldsValidation } from '../use-localized-fields-validation/use-localized-fields-validation.hook';
import { transposeRelationshipsForConfigEntitySchema } from './transpose-relationships-for-config-entity';

interface ValidateAndSetErrorsProps {
  // array of error keywords to remove errors with a particular keyword from validation
  filteredErrorKeywords?: string[];
}

/**
 * useEntityFormError manages form validation and error handling for the entire entity form (root data and root schema) via the configUserFeedbackStore
 *
 * This hook provides the `validateEntityData` function to validate the form data against the schema, and to set the filteredErrorKeywords - handling the display of validation errors using the `useConfigUserFeedback` hook.
 */
export const useEntityFormError = () => {
  const {
    setShowFormValidation,
    additionalValidationErrors,
    setFilteredErrorKeywords,
    filteredErrorKeywords,
    setConfigError,
    showFormValidation,
    setShowErrorBanner,
    resetConfigUserFeedbackStore,
  } = useConfigUserFeedback((state) => state);

  const { frontendValidation } = useConfigApp();
  // Gracefully handle tests or environments where frontendValidation / uriSchemePolicy is not provided
  const ajv = useMemo(() => {
    const { allowed } = frontendValidation.uriSchemePolicy;
    return createConfigurableAjv(allowed);
  }, [frontendValidation.uriSchemePolicy]);

  const { validateAllLocalizedFields } = useLocalizedFieldsValidation();
  const formatConfigErrors = useFormatFormErrors();

  const { rootSchema, entityRootData, configEntitySchema, entity } =
    useEntityDetailsProps();

  /**
   * Validates the form data and sets the configError in the configUserFeedbackStore
   * If there are no errors, it unsubscribes from error updates (showFormValidation is false), resets the config error,
   *
   * @param {string[]} [params.filteredErrorKeywords] - An optional array of error keywords to exclude from validation.
   *   If provided, only errors whose `keyword` property is not included in this array will be considered.
   *
   * @returns {boolean} `true` if the validation is successful (no errors), `false` otherwise.
   */
  const featureFlags = getAllFeatureFlags();

  const validateEntityData = useCallback(
    ({
      filteredErrorKeywords: newFilteredErrorKeywords,
    }: ValidateAndSetErrorsProps = {}): boolean => {
      if (newFilteredErrorKeywords) {
        // we only want to setFilteredErrorKeywords if they are passed on initial validation check - on re-validation checks they should not be reset
        setFilteredErrorKeywords(newFilteredErrorKeywords);
      }

      const isUsingConfigEntity = isUsingConfigAppBackend({
        entityLevelOverride: entity?.isUsingConfigAppBackend,
      });

      const transposedSchema =
        transposeRelationshipsForConfigEntitySchema(configEntitySchema);

      const rootEntityAttributes = isUsingConfigEntity
        ? entityRootData?.attributes?.domainEntityAttributes
        : entityRootData?.attributes;

      const transformedConfigEntitySchema = computeSchema(
        transposedSchema,
        rootEntityAttributes,
        [applyConditionalLogic],
        {
          rootData: rootEntityAttributes,
          featureFlags,
        },
      );

      /**
       * Removes empty objects from the entityRootData before validation
       * This is to ensure that the validation does not fail due to optional empty objects that have required properties
       * that are not present in the data
       * The same sanitization is done on the data before it is sent to the backend, so the data that is validated is the same as the data that is sent to the backend
       */
      const sanitizedData = entityRootData
        ? removeNullAndEmptyObjects(entityRootData)
        : entityRootData;

      // Validate the data against the schema and get the potential errors
      const { formErrors } = validateData({
        schema: isUsingConfigEntity
          ? transformedConfigEntitySchema
          : rootSchema,
        data: sanitizedData,
        // if newFilterOutKeywords are passed used these, if they are not passed it is a re-validation check so we should use the filteredErrorKeywords stored in state
        filteredErrorKeywords:
          newFilteredErrorKeywords || filteredErrorKeywords,
        ajv,
      });

      const additionalErrorsFlattened = Object.values(
        additionalValidationErrors,
      ).flat();

      if (!formErrors.length && !additionalErrorsFlattened.length) {
        resetConfigUserFeedbackStore();
        return true;
      }

      const errors = formatConfigErrors(formErrors);

      const error = {
        type: ConfigErrorType.FormValidationError,
        errors: [...errors, ...additionalErrorsFlattened],
      };

      setConfigError(error);
      setShowErrorBanner(true);
      setShowFormValidation(true);

      return false;
    },
    // TODO:: configEntitySchema being in dependency array is causing infinite loop - to fix this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      additionalValidationErrors,
      rootSchema,
      entityRootData,
      filteredErrorKeywords,
      formatConfigErrors,
      setConfigError,
      setShowErrorBanner,
      setShowFormValidation,
      setFilteredErrorKeywords,
      resetConfigUserFeedbackStore,
      ajv,
    ],
  );

  /**
   * If showFormValidation is true, continuously re-validate the form and update the config errors without overriding the filterErrorKeywords to provide live feedback to the user on entityRootData change.
   * once the formErrors are cleared, showFormValidation is set to false
   */

  // Track whether we've already validated localized fields for this validation session
  const hasValidatedLocalizedFields = useRef(false);

  useEffect(() => {
    if (showFormValidation) {
      validateEntityData();

      // Validate localized fields once per validation session (when showFormValidation is true)
      if (!hasValidatedLocalizedFields.current) {
        validateAllLocalizedFields();
        hasValidatedLocalizedFields.current = true;
      }
    } else {
      // Reset the flag when validation is turned off
      hasValidatedLocalizedFields.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityRootData,
    validateEntityData,
    showFormValidation,
    // Note: validateAllLocalizedFields is intentionally excluded from dependencies
    // to avoid infinite loops since it changes on every render
  ]);

  //  handle localized validation on data changes so localized fields are re-validated synchronously when data changes
  useEffect(() => {
    if (showFormValidation && hasValidatedLocalizedFields.current) {
      // Only re-validate if we've already done the initial validation
      // This prevents the initial validation from running twice
      validateAllLocalizedFields();
    }
  }, [entityRootData, showFormValidation, validateAllLocalizedFields]);

  return {
    validateEntityData,
  };
};
